import { useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { CheckCircle2, CircleAlert, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";

const COLS_BANQUE = [["date", "Date"], ["description", "Description"], ["debit", "Débit"], ["credit", "Crédit"], ["solde", "Solde"]];
const COLS_RESA = [["date", "Date"], ["code", "Code"], ["logement", "Logement"], ["nuits", "Nuits"], ["loyer", "Loyer"], ["frais_menage", "Ménage"], ["commission", "Commission"], ["taxes_versees", "Taxes"], ["total_verse", "Total versé"]];
const NUM = ["debit", "credit", "solde", "loyer", "frais_menage", "commission", "taxes_versees", "total_verse"];

const Mapping = ({ champs }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="apercu-mapping">
    {champs.map((c) => (
      <li key={c.champ} className="flex items-center gap-2">
        {c.colonne ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <CircleAlert className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
        <span className="text-slate-600">{c.champ}</span>
        <span className={`ml-auto font-mono text-xs ${c.colonne ? "text-slate-900" : "text-slate-400"}`}>{c.colonne || "non trouvée"}</span>
      </li>
    ))}
  </ul>
);

const Preview = ({ rows, cols }) => (
  <div className="overflow-x-auto rounded-md border">
    <SmartTable id="apercu-import" className="w-full text-xs" data-testid="apercu-rows">
      <thead className="bg-slate-50"><tr>{cols.map(([k, l]) => <th key={k} className={`px-2 py-1.5 font-mono uppercase text-[10px] text-slate-500 ${NUM.includes(k) ? "text-right" : "text-left"}`}>{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t">{cols.map(([k]) => <td key={k} className={`px-2 py-1.5 whitespace-nowrap ${NUM.includes(k) ? "text-right tabular" : ""}`}>{NUM.includes(k) ? (r[k] == null ? "—" : money(r[k])) : r[k] || "—"}</td>)}</tr>
      ))}</tbody>
    </SmartTable>
  </div>
);

export const ApercuDialog = ({ target, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) return;
    setData(null); setError("");
    const fd = new FormData();
    fd.append("type", target.type);
    fd.append("file", target.file);
    api.post("/imports/apercu", fd).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [target]);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      {target && (
        <DialogContent className="max-w-3xl" data-testid="apercu-dialog">
          <DialogHeader>
            <DialogTitle>Vérification des colonnes · {target.file.name}</DialogTitle>
            <DialogDescription>Rien n'est importé tant que vous ne cliquez pas sur « Importer ».</DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-red-600" data-testid="apercu-error">{error}</p>}
          {!data && !error && <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />}
          {data && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              <p className="text-sm" data-testid="apercu-summary">Format détecté : <b>{data.format}</b> · <b>{data.nb_lignes}</b> ligne(s) reconnue(s)</p>
              {data.champs.length > 0 && <Mapping champs={data.champs} />}
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Premières lignes telles qu'elles seront importées</p>
              <Preview rows={data.lignes} cols={target.type === "banque" ? COLS_BANQUE : COLS_RESA} />
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
